// SPDX-FileCopyrightText: 2023 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { Trans, useTranslation } from 'react-i18next';
import React, { useCallback } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { closeDialog } from 'store/reducers/projectLockedSlice';
import { fetchProjects } from 'store/reducers/projectsSlice';
import useOpenProjectWithLock from 'hooks/useOpenProjectWithLock';
import useProjectsMetadata from 'hooks/useProjectsMetadata';
import { AcknowledgeStatus } from '@tls/sw91-communication/types/com.base';
import { ToastByStatus } from 'components/treactui-template/organisms/toast/ToastMessage';
import { useProjectLockApi } from 'hooks/useProjectLockApi';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from 'utilities/router/AppRoutes';
import styled from 'styled-components';
import { Dialog, FooterButton } from '@tls/treact-ui';
import { FlagRenderer } from '@tls/treact-ui/flags/all';

export default function ProjectLockedDialog() {
  const { t } = useTranslation();
  const { showDialog, projectLockResponse, projectIdToLock, intent } = useAppSelector(a => a.projectLocked);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { projects } = useProjectsMetadata();
  const projectName = projects.find(p => p.projectId === projectIdToLock)?.projectName ?? '';
  const lockedProjectName =
    projects.find(p => p.projectId === projectLockResponse?.currentProjectLockedByUser)?.projectName ?? t('deleted');

  const { lock, unlock, loading } = useProjectLockApi();
  const { openProjectWithLock } = useOpenProjectWithLock();

  const onClose = useCallback(() => dispatch(closeDialog()), [dispatch]);
  const switchProjects = useCallback(async () => {
    if (!projectLockResponse?.currentProjectLockedByUser || !projectIdToLock) return;
    await unlock(projectLockResponse?.currentProjectLockedByUser);

    if (intent && intent !== 'openProject') {
      // single refresh after the whole switch, otherwise a stale response can win
      const opened = await openProjectWithLock(projectIdToLock, intent, false);
      if (!opened) return;
    } else {
      const lockResult = await lock(projectIdToLock, intent);
      if (!lockResult) return;

      navigate(AppRoutes.ProjectLabelFullPath(projectIdToLock));
    }

    dispatch(fetchProjects());
    onClose();
  }, [
    dispatch,
    intent,
    lock,
    navigate,
    onClose,
    openProjectWithLock,
    projectIdToLock,
    projectLockResponse?.currentProjectLockedByUser,
    unlock,
  ]);

  const copyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(projectLockResponse?.lockedByUser?.userName ?? '');
    ToastByStatus(
      200,
      t('project.lock.copy.title'),
      undefined,
      t('project.lock.copy.success', { userName: projectLockResponse?.lockedByUser?.userName })
    );
  }, [projectLockResponse?.lockedByUser?.userName, t]);

  return (
    <Dialog enableClosing open={showDialog} size='small' onClose={onClose} data-testid='projects-lock-dialog'>
      <Dialog.DialogHeader label={t('project.lock.title')} showCloseButton />
      <Dialog.DialogContent>
        <div data-testid='projects-lock-dialog-content'>
          {getDetailsText(
            {
              projectName,
              lockedProjectName,
              username: projectLockResponse?.lockedByUser?.userName,
            },
            projectLockResponse?.ackMessage?.status?.status
          )}
        </div>
      </Dialog.DialogContent>
      <Dialog.Footer
        left={[
          projectLockResponse?.ackMessage?.status?.status === AcknowledgeStatus.PROJECT_LOCKED && (
            <FooterButton
              key='secondary'
              variant='secondary'
              label={t('project.lock.copy.toClipboard')}
              onClick={copyToClipboard}
              data-testid='projects-lock-dialog-copy-user-button'
            />
          ),
        ]}
        right={[
          projectLockResponse?.ackMessage?.status?.status === AcknowledgeStatus.MAX_LOCKS_EXCEEDED && (
            <FooterButton
              key='max-locks'
              disabled={loading}
              iconKey={loading ? 'IconLoading' : undefined}
              onClick={switchProjects}
              label={t('project.lock.switch')}
              data-testid='projects-lock-dialog-switch-project-button'
            />
          ),
        ]}
      />
    </Dialog>
  );
}

interface DetailsTextParams {
  projectName: string;
  lockedProjectName: string;
  username?: string;
}

function getDetailsText({ projectName, lockedProjectName, username }: DetailsTextParams, status?: AcknowledgeStatus) {
  if (status === AcknowledgeStatus.MAX_LOCKS_EXCEEDED) {
    return (
      <>
        <Trans i18nKey={'project.lock.maxLocks'} values={{ projectName, lockedProjectName, username }} />
        <SameLine>
          <FlagRenderer flag='warningMessage' width='1.5rem' />
          <Trans i18nKey={'project.lock.stopsTraining'} />
        </SameLine>
      </>
    );
  }
  return <Trans i18nKey={getErrorMessageByStatus(status)} values={{ projectName, lockedProjectName, username }} />;
}

function getErrorMessageByStatus(status?: AcknowledgeStatus) {
  switch (status) {
    case AcknowledgeStatus.PROJECT_LOCKED:
      return 'project.lock.isLockedBy';
    default:
      return 'project.lock.error';
  }
}

const SameLine = styled.div`
  display: flex;
  align-items: center;
  margin-top: 1rem;
  column-gap: 0.5rem;
`;
